import { Minus, Plus, Trash2 } from "lucide-react";
import { money } from "@/lib/api";
import { cn } from "@/lib/utils";

const PRESETS = {
  villa: ["Villa rental", "Chef & staff", "Food & drink", "Transfers"],
  yacht: ["Charter fee", "APA (fuel, food, port fees)", "Crew gratuity", "Transfers"],
  jet: ["Flight hours", "Fees & taxes", "Catering", "Ground transport"],
  chalet: ["Chalet rental", "Chef & staff", "Ski passes & guides", "Transfers"],
  events: ["Venue & tables", "Bottle service", "Security", "Transport"],
  talent: ["Artist / DJ fee", "Production & sound", "Travel & rider", "Venue"],
  dining: ["Private chef or venue", "Wine & drinks", "Service", "Transport"],
  other: ["Main cost", "Extras"],
};
export const CURRENCIES = ["EUR", "USD", "GBP", "CHF", "AED"];
const SYMBOL = { EUR: "€", USD: "$", GBP: "£", CHF: "CHF", AED: "AED" };
const grouped = (v) => {
  if (v === "" || v == null) return "";
  const [i, d] = String(v).split(".");
  return Number(i || 0).toLocaleString("en-GB") + (d !== undefined ? `.${d}` : "");
};

export const MoneyInput = ({ value, onChange, currency, readOnly, className, ...rest }) => (
  <div className={cn("relative", className)}>
    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{SYMBOL[currency] || currency}</span>
    <input type="text" inputMode="decimal" autoComplete="off" readOnly={readOnly} placeholder="0" value={grouped(value)}
      onChange={(e) => { const raw = e.target.value.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1"); const [i, d] = raw.split("."); onChange(d !== undefined ? `${i}.${d.slice(0, 2)}` : i); }}
      className={cn("field text-right tabular-nums", (SYMBOL[currency] || currency).length > 1 ? "pl-14" : "pl-8", readOnly && "cursor-default bg-secondary/40")} {...rest} />
  </div>
);
export const roomLabel = (type) => (type === "yacht" ? "Cabins" : "Bedrooms");
export const nightsBetween = (a, b) => (a && b ? Math.max(Math.round((new Date(b) - new Date(a)) / 86400000), 0) : 0);

export const Stepper = ({ label, hint, value, min = 0, max = 30, onChange, testId }) => (
  <div className="rounded-[12px] border border-border p-4" data-testid={testId}>
    <p className="field-label mb-1">{label}</p>
    {hint && <p className="mb-3 text-xs text-muted-foreground">{hint}</p>}
    <div className="flex items-center gap-4">
      <button type="button" className="btn-outline h-11 w-11 px-0" aria-label={`Fewer ${label}`} disabled={value <= min} onClick={() => onChange(value - 1)} data-testid={`${testId}-minus`}><Minus className="h-4 w-4" /></button>
      <span className="min-w-[2ch] text-center font-display text-4xl" aria-live="polite" data-testid={`${testId}-value`}>{value}</span>
      <button type="button" className="btn-outline h-11 w-11 px-0" aria-label={`More ${label}`} disabled={value >= max} onClick={() => onChange(value + 1)} data-testid={`${testId}-plus`}><Plus className="h-4 w-4" /></button>
    </div>
  </div>
);

export const CostLines = ({ type, items, currency, onChange }) => {
  const update = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const suggestions = (PRESETS[type] || PRESETS.other).filter((p) => !items.some((it) => it.label === p));
  return (
    <div className="space-y-3" data-testid="cost-lines">
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-[1fr_150px_44px] gap-2 sm:grid-cols-[1fr_200px_44px]">
          <input className="field" maxLength={40} value={it.label} placeholder="What's it for?" aria-label={`Cost ${i + 1} name`} onChange={(e) => update(i, "label", e.target.value)} data-testid={`cost-label-${i}`} />
          <MoneyInput value={it.amount} currency={currency} aria-label={`Cost ${i + 1} amount in ${currency}`} onChange={(v) => update(i, "amount", v)} data-testid={`cost-amount-${i}`} />
          <button type="button" className="btn-outline h-12 w-11 px-0" aria-label={`Remove ${it.label || "cost"}`} onClick={() => onChange(items.filter((_, j) => j !== i))} data-testid={`cost-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        {suggestions.map((p) => <button key={p} type="button" className="chip" onClick={() => onChange([...items, { label: p, amount: "" }])} data-testid={`cost-add-${p.split(" ")[0].toLowerCase()}`}>+ {p}</button>)}
        {items.length < 12 && <button type="button" className="chip" onClick={() => onChange([...items, { label: "", amount: "" }])} data-testid="cost-add-custom">+ Custom line</button>}
      </div>
    </div>
  );
};

const Row = ({ label, value, strong, testId }) => (
  <div className={cn("flex items-baseline justify-between gap-4 py-2", strong && "border-t border-border pt-4")}>
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className={cn(strong ? "font-display text-3xl text-primary" : "font-semibold")} data-testid={testId}>{value}</span>
  </div>
);

export const LiveSummary = ({ f }) => {
  const total = Number(f.budget_total) || 0;
  const slots = Math.max(Number(f.cohost_slots) || 1, 1);
  const nights = nightsBetween(f.start_date, f.end_date);
  const rooms = Number(f.bedrooms) || 0;
  const share = total / slots;
  return (
    <aside className="surface p-6 lg:sticky lg:top-24" aria-label="Live budget summary" data-testid="live-summary">
      <p className="eyebrow">The split</p>
      <Row label="Total budget" value={total ? money(total, f.currency) : "—"} testId="summary-total" />
      <Row label="Co-host places" value={slots} testId="summary-slots" />
      {nights > 0 && <Row label={`Per night (${nights})`} value={total ? money(total / nights, f.currency) : "—"} testId="summary-per-night" />}
      {rooms > 0 && <Row label={`Per ${roomLabel(f.type).toLowerCase().slice(0, -1)}`} value={total ? money(total / rooms, f.currency) : "—"} testId="summary-per-room" />}
      <Row label="Guest places" value={Number(f.guest_places) || 0} testId="summary-guests" />
      <Row strong label={f.split_mode === "equal" ? "Each co-host pays" : "Average per co-host"} value={total ? money(share, f.currency) : "—"} testId="summary-share" />
      <p className="mt-3 text-xs text-muted-foreground">
        {f.split_mode === "equal" ? "Every co-host, you included, pays an equal share. Shares are confirmed individually in the planning space." : "You'll set each co-host's share in the planning space once they join."} Payments are arranged outside the platform.
      </p>
    </aside>
  );
};
